import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiUrl, formatApiError } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Badge, Button, Card } from "../../components/ui-kit";
import { FileText, CheckCircle, XCircle, Eye } from "@phosphor-icons/react";

const DOC_LABEL = {
  driver_license: "Permis de conduire",
  vehicle_registration: "Carte grise",
  id_card: "Piece d'identite",
  insurance: "Assurance",
  vehicle_plate_photo: "Photo de la plaque",
};

export default function AdminDocs() {
  const [params] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/driver-documents", { params: { status_filter: statusFilter } });
      setDocs(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const selectedDriver = params.get("driver");
  const visibleDocs = useMemo(
    () => selectedDriver ? docs.filter((d) => d.driver_id === selectedDriver) : docs,
    [docs, selectedDriver]
  );

  const approve = async (id) => {
    try {
      await api.post(`/admin/driver-documents/${id}/approve`);
      toast.success("Document approuve");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (id) => {
    const note = window.prompt("Motif du refus ?") || "";
    try {
      await api.post(`/admin/driver-documents/${id}/reject`, null, { params: { note } });
      toast.success("Document refuse");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const previewUrl = (id) => apiUrl(`/files/driver-docs/${id}`);

  return (
    <AdminShell title="Documents">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-3xl font-black tracking-tighter sm:text-4xl" data-testid="docs-title">
            <FileText weight="fill" className="text-primary" /> Validation documents
          </h1>
          <p className="text-sm text-muted-foreground">Permis, carte grise, piece d'identite, assurance et plaque.</p>
        </div>
        <Badge tone="primary">{visibleDocs.length} document(s)</Badge>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {["ALL", "PENDING_VALIDATION", "APPROVED", "REJECTED", "SUPERSEDED"].map((status) => (
          <button
            key={status}
            type="button"
            onClick={() => setStatusFilter(status)}
            className={`rounded-full border px-3 py-2 text-[10px] font-bold uppercase tracking-widest ${
              statusFilter === status ? "border-primary bg-primary text-primary-foreground" : "border-white/10 bg-white/5 text-muted-foreground"
            }`}
          >
            {status === "ALL" ? "Tous" : status}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {loading && <div className="text-sm font-bold uppercase tracking-widest">Chargement...</div>}
        {visibleDocs.map((d) => (
          <Card key={d.id} className="p-5" data-testid={`doc-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  {DOC_LABEL[d.doc_type] || d.doc_type}
                </div>
                <h3 className="truncate font-heading text-lg font-black">{d.driver_name || "-"}</h3>
                <div className="truncate font-mono text-xs text-muted-foreground">{d.driver_email}</div>
                <div className="font-mono text-xs text-muted-foreground">{d.driver_phone}</div>
                <div className="mt-2 truncate text-[10px] text-muted-foreground">
                  {d.original_filename} - {Math.round((d.size || 0) / 1024)} ko
                </div>
                <div className="mt-2">
                  <Badge tone={d.status === "APPROVED" ? "success" : d.status === "REJECTED" ? "danger" : "warning"}>{d.status}</Badge>
                </div>
              </div>
              <button
                onClick={() => setPreview(d)}
                className="shrink-0 rounded-full glass-strong p-2 press-down"
                data-testid={`preview-${d.id}`}
                title="Voir le document"
              >
                <Eye weight="bold" size={18} />
              </button>
            </div>
            {d.status === "PENDING_VALIDATION" && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="secondary" onClick={() => approve(d.id)} data-testid={`approve-${d.id}`}>
                  <CheckCircle weight="fill" /> Approuver
                </Button>
                <Button variant="danger" onClick={() => reject(d.id)} data-testid={`reject-${d.id}`}>
                  <XCircle weight="fill" /> Refuser
                </Button>
              </div>
            )}
          </Card>
        ))}
        {!loading && visibleDocs.length === 0 && (
          <Card className="p-8 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground" data-testid="docs-empty">
            Aucun document
          </Card>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm"
          onClick={() => setPreview(null)}
          data-testid="doc-preview"
        >
          <div className="max-h-[92vh] w-full max-w-3xl overflow-auto rounded-3xl glass-strong p-2" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-3">
              <div className="font-heading text-lg font-black">{DOC_LABEL[preview.doc_type] || preview.doc_type}</div>
              <button onClick={() => setPreview(null)} className="rounded-full glass-strong p-2" data-testid="close-preview">
                <XCircle size={20} weight="bold" />
              </button>
            </div>
            {preview.content_type?.startsWith("image/") ? (
              <img src={previewUrl(preview.id)} alt={preview.doc_type} className="max-h-[75vh] w-full rounded-2xl object-contain" />
            ) : (
              <iframe src={previewUrl(preview.id)} title="document" className="h-[75vh] w-full rounded-2xl bg-white" />
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
